// Universal AI Tuner Pro — premium About page
// Story-driven, trustworthy, dark-cinematic. Matches UniversalHome aesthetic.
import React from 'react';
import { motion } from 'framer-motion';
import {
  Wrench, Target, Zap, Database, Sparkles, Users, ShieldCheck,
  Rocket, CheckCircle2, XCircle, ArrowRight, Quote, Car, Gauge,
  Cpu, UploadCloud, Heart, Award, Lock, RefreshCw,
} from 'lucide-react';

const FADE_UP = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-80px' },
  transition: { duration: 0.6, ease: 'easeOut' },
};

const FOUNDER_IMG  = 'https://images.pexels.com/photos/3807277/pexels-photo-3807277.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1200';
const ENGINE_IMG   = 'https://images.pexels.com/photos/15393572/pexels-photo-15393572.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1200';
const DYNO_IMG     = 'https://images.unsplash.com/photo-1635770310991-fbb7a6ddc4fa?crop=entropy&cs=srgb&fm=jpg&w=1200&q=80';
const BINARY_IMG   = 'https://images.unsplash.com/photo-1764258057604-94e8e6ca092f?crop=entropy&cs=srgb&fm=jpg&w=1200&q=80';

// ========== HERO ==========
function AboutHero() {
  return (
    <section className="relative min-h-[70vh] flex items-center overflow-hidden bg-[#0A0A0A] border-t border-[#18181B]">
      <div className="absolute inset-0">
        <img src={BINARY_IMG} alt="" className="w-full h-full object-cover opacity-25" loading="lazy" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0A] via-[#0A0A0A]/90 to-[#0A0A0A]/70" />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent to-[#0A0A0A]" />
      </div>
      <motion.div
        className="absolute top-1/3 right-1/4 w-80 h-80 rounded-full bg-[#FF5E1A]/10 blur-[110px]"
        animate={{ opacity: [0.3, 0.6, 0.3], y: [0, -20, 0] }}
        transition={{ duration: 7, repeat: Infinity }}
      />
      <div className="relative z-10 container mx-auto px-6 py-20">
        <motion.div {...FADE_UP} className="max-w-3xl">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 mb-6 bg-[#FF5E1A]/10 border border-[#FF5E1A]/30 rounded-sm">
            <Sparkles className="w-3.5 h-3.5 text-[#FF5E1A]" />
            <span className="text-xs font-['JetBrains_Mono'] uppercase tracking-[0.18em] text-[#FF5E1A]">Our Story</span>
          </div>
          <h1 className="font-['Outfit'] text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter leading-[1.05] text-white mb-6">
            Building the Future of{' '}
            <span className="bg-gradient-to-r from-[#007AFF] via-[#4DA3FF] to-[#FF5E1A] bg-clip-text text-transparent">
              ECU Tuning.
            </span>
          </h1>
          <p className="text-lg sm:text-xl text-zinc-300 max-w-2xl leading-relaxed">
            AI Tuner Pro was created by real tuners, for real tuners — to remove the guesswork
            and templates from <span className="text-white font-semibold">every ECU on the planet.</span>
          </p>
        </motion.div>
      </div>
    </section>
  );
}

// ========== FOUNDER STORY ==========
function FounderStory() {
  const beats = [
    {
      icon: Wrench,
      tag: 'The Start',
      title: 'Thirty years under hoods — zero hours in hex editors.',
      text: 'I\'m Todd (@Fast_imports3). Factory-certified Yamaha tech at 18, WyoTech-trained, raced two-stroke jetskis, built rotary engines, lived under European hoods. When I wanted to tune my BMW N51 on E85, I had the mechanical instinct — but didn\'t know a lambda table from a load axis.',
      color: '#007AFF',
    },
    {
      icon: XCircle,
      tag: 'The Frustration',
      title: 'Every existing tool felt like a dead end.',
      text: 'Pre-built map packs only covered a handful of ECUs. Open-source definitions were scattered, outdated, or outright wrong. Paid services charged $500+ for generic tunes you couldn\'t iterate on. If your vehicle wasn\'t on the "supported" list — tough luck. The tuning world had been gatekept for decades.',
      color: '#FF5E1A',
    },
    {
      icon: Sparkles,
      tag: 'The Aha',
      title: '27 tune revisions later, the pattern became obvious.',
      text: 'I burned through 22 E85 revisions and 5 pump-gas revisions before cracking it — flat 2.5x fuel scaling, the EGS transmission module owning kickdown, lambda targets living outside the cal block. Every failure taught me something no documentation covered. That\'s when it clicked: an AI that watches every byte change and every datalog result — paired with a growing database of real, user-contributed maps — could do this for ANY binary, not just mine.',
      color: '#007AFF',
    },
    {
      icon: Target,
      tag: 'The Goal',
      title: 'Make powerful, safe, iterative tuning accessible to everyone.',
      text: 'Cars, trucks, motorcycles, marine, tractors, industrial equipment — if it has an ECU binary, you should be able to upload it, chat your goals, and iterate with real datalogs until the tune is YOURS. Not a template. Not a guess. A calibration built on your fuel, your hardware, your numbers.',
      color: '#FF5E1A',
    },
  ];
  return (
    <section className="py-24 bg-[#0A0A0A]">
      <div className="container mx-auto px-6">
        <motion.div {...FADE_UP} className="max-w-2xl mb-14">
          <div className="text-xs font-['JetBrains_Mono'] uppercase tracking-[0.2em] text-[#007AFF] mb-3">// Founder Story</div>
          <h2 className="font-['Outfit'] text-3xl sm:text-5xl font-black tracking-tighter text-white mb-4">
            Built the hard way. Shared with everyone.
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          {/* Founder photo */}
          <motion.div {...FADE_UP} className="lg:col-span-5 relative">
            <div className="relative overflow-hidden rounded-sm border border-[#27272A] aspect-[4/5] bg-[#121212]">
              <img src={FOUNDER_IMG} alt="Founder in the shop" className="w-full h-full object-cover" loading="lazy" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-transparent to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-6">
                <div className="text-xs font-['JetBrains_Mono'] uppercase tracking-[0.2em] text-[#FF5E1A] mb-2">Founder</div>
                <div className="font-['Outfit'] text-2xl font-black text-white">Todd Miller</div>
                <div className="text-sm text-zinc-400 font-['JetBrains_Mono']">@Fast_imports3 • 30yr tech • BMW N51 platform</div>
              </div>
            </div>
            {/* Accent corners */}
            <div className="absolute -top-1.5 -left-1.5 w-6 h-6 border-t-2 border-l-2 border-[#FF5E1A]" />
            <div className="absolute -bottom-1.5 -right-1.5 w-6 h-6 border-b-2 border-r-2 border-[#007AFF]" />
          </motion.div>

          {/* Story beats */}
          <div className="lg:col-span-7 space-y-5">
            {beats.map((b, i) => (
              <motion.div
                key={b.tag}
                {...FADE_UP}
                transition={{ duration: 0.6, delay: i * 0.08 }}
                className="group p-6 bg-[#121212] border border-[#27272A] rounded-sm hover:border-[#007AFF]/40 transition-all"
              >
                <div className="flex items-start gap-4">
                  <div
                    className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-sm border border-[#27272A] group-hover:border-transparent transition-colors"
                    style={{ backgroundColor: `${b.color}10` }}
                  >
                    <b.icon className="w-5 h-5" style={{ color: b.color }} />
                  </div>
                  <div className="min-w-0">
                    <div className="text-[10px] font-['JetBrains_Mono'] uppercase tracking-[0.2em] mb-1" style={{ color: b.color }}>{b.tag}</div>
                    <h3 className="font-['Outfit'] text-lg font-bold text-white mb-2 leading-tight">{b.title}</h3>
                    <p className="text-sm text-zinc-400 leading-relaxed">{b.text}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

// ========== MISSION ==========
function Mission() {
  return (
    <section className="py-24 bg-gradient-to-b from-[#0A0A0A] via-[#050505] to-[#0A0A0A] relative overflow-hidden">
      <div className="absolute inset-0 pointer-events-none" style={{ backgroundImage: 'repeating-linear-gradient(0deg, rgba(0,122,255,0.03) 0px, rgba(0,122,255,0.03) 1px, transparent 1px, transparent 3px)' }} />
      <div className="relative container mx-auto px-6">
        <motion.div {...FADE_UP} className="max-w-4xl mx-auto text-center">
          <div className="text-xs font-['JetBrains_Mono'] uppercase tracking-[0.2em] text-[#FF5E1A] mb-4">// Our Mission</div>
          <div className="relative p-10 sm:p-14 bg-[#121212]/60 backdrop-blur border border-[#27272A] rounded-sm">
            <Quote className="absolute -top-5 left-8 w-10 h-10 text-[#FF5E1A] fill-[#FF5E1A] bg-[#0A0A0A] p-2 rounded-sm border border-[#FF5E1A]/40" />
            <p className="font-['Outfit'] text-2xl sm:text-3xl lg:text-4xl font-bold text-white leading-snug tracking-tight">
              To create the world's first truly <span className="text-[#FF5E1A]">universal ECU tuning platform</span> where the AI learns from every upload and every definition you share — making tuning <span className="text-[#007AFF]">faster, safer, and better</span> for every vehicle on Earth.
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

// ========== WHY HYBRID ==========
function HybridAI() {
  const pillars = [
    {
      icon: Database,
      tag: 'Database',
      title: 'Instant speed on known ECUs.',
      text: 'Thousands of pre-loaded and community-verified tables. Known binaries load in seconds with fuel maps, timing, boost, limits, and DTCs already labeled.',
      stat: '1,000+',
      statLabel: 'Known tables',
      color: '#007AFF',
      img: BINARY_IMG,
    },
    {
      icon: Sparkles,
      tag: 'AI Discovery',
      title: 'Full discovery on unknown binaries.',
      text: 'Never-before-seen ECU? Our AI reasons from first principles — MAF curves, timing patterns, byte entropy — to locate every map and constant from scratch.',
      stat: '∞',
      statLabel: 'Binary formats',
      color: '#FF5E1A',
      img: ENGINE_IMG,
    },
    {
      icon: Users,
      tag: 'Community',
      title: 'You make the database smarter.',
      text: 'Upload Damos, A2L, XDF, or your own annotations. Earn credits + contributor badges. Every shared definition grows the tuning internet for everyone.',
      stat: 'Shared',
      statLabel: 'Forever',
      color: '#007AFF',
      img: DYNO_IMG,
    },
  ];
  return (
    <section className="py-24 bg-[#0A0A0A]">
      <div className="container mx-auto px-6">
        <motion.div {...FADE_UP} className="max-w-2xl mb-14">
          <div className="text-xs font-['JetBrains_Mono'] uppercase tracking-[0.2em] text-[#007AFF] mb-3">// Why Hybrid</div>
          <h2 className="font-['Outfit'] text-3xl sm:text-5xl font-black tracking-tighter text-white mb-4">
            Three engines. One calibration.
          </h2>
          <p className="text-lg text-zinc-400 leading-relaxed">
            Database speed. AI reasoning. Community knowledge. Each makes the other stronger — and every upload teaches the system.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {pillars.map((p, i) => (
            <motion.div
              key={p.tag}
              {...FADE_UP}
              transition={{ duration: 0.6, delay: i * 0.1 }}
              className="group relative overflow-hidden bg-[#121212] border border-[#27272A] rounded-sm hover:border-[#007AFF]/40 transition-all hover:-translate-y-1"
            >
              <div className="aspect-[16/9] overflow-hidden relative">
                <img src={p.img} alt="" className="w-full h-full object-cover opacity-40 group-hover:opacity-55 group-hover:scale-105 transition-all duration-700" loading="lazy" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#121212] via-[#121212]/60 to-transparent" />
                <div className="absolute top-4 left-4">
                  <div className="w-10 h-10 flex items-center justify-center rounded-sm bg-[#0A0A0A]/80 backdrop-blur border" style={{ borderColor: `${p.color}66` }}>
                    <p.icon className="w-5 h-5" style={{ color: p.color }} />
                  </div>
                </div>
                <div className="absolute bottom-4 right-4 text-right">
                  <div className="font-['Outfit'] text-3xl font-black text-white leading-none">{p.stat}</div>
                  <div className="text-[10px] font-['JetBrains_Mono'] uppercase tracking-wider text-zinc-400 mt-1">{p.statLabel}</div>
                </div>
              </div>
              <div className="p-6">
                <div className="text-[10px] font-['JetBrains_Mono'] uppercase tracking-[0.2em] mb-2" style={{ color: p.color }}>{p.tag}</div>
                <h3 className="font-['Outfit'] text-lg font-bold text-white mb-2">{p.title}</h3>
                <p className="text-sm text-zinc-400 leading-relaxed">{p.text}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ========== WHAT SETS US APART ==========
function Differentiators() {
  const rows = [
    { feature: 'Vehicle coverage', us: 'Any vehicle with an ECU binary', them: 'Pre-supported ECU list only', usIcon: CheckCircle2, themIcon: XCircle },
    { feature: 'Approach', us: 'AI discovery + hybrid database', them: 'Generic pre-built map packs', usIcon: CheckCircle2, themIcon: XCircle },
    { feature: 'Refinement', us: 'Iterative chat + real datalog refinement', them: 'One-and-done flash', usIcon: CheckCircle2, themIcon: XCircle },
    { feature: 'Knowledge growth', us: 'Crowd-sourced, grows every day', them: 'Static, maintained by one vendor', usIcon: CheckCircle2, themIcon: XCircle },
    { feature: 'Safety', us: 'Health gates + rev/boost/AFR validation', them: 'Trust the template, hope for the best', usIcon: CheckCircle2, themIcon: XCircle },
    { feature: 'Lock-in', us: 'Your bin, your data, your control', them: 'Locked to proprietary tool + license', usIcon: CheckCircle2, themIcon: XCircle },
  ];
  return (
    <section className="py-24 bg-gradient-to-b from-[#0A0A0A] via-[#050505] to-[#0A0A0A]">
      <div className="container mx-auto px-6">
        <motion.div {...FADE_UP} className="max-w-2xl mb-12">
          <div className="text-xs font-['JetBrains_Mono'] uppercase tracking-[0.2em] text-[#FF5E1A] mb-3">// What Sets Us Apart</div>
          <h2 className="font-['Outfit'] text-3xl sm:text-5xl font-black tracking-tighter text-white mb-4">
            Six things you won't find anywhere else.
          </h2>
        </motion.div>

        <motion.div {...FADE_UP} className="max-w-5xl mx-auto overflow-hidden border border-[#27272A] rounded-sm">
          {/* Header */}
          <div className="grid grid-cols-[1.4fr_1fr_1fr] bg-[#0A0A0A] border-b border-[#27272A]">
            <div className="p-4 text-[10px] font-['JetBrains_Mono'] uppercase tracking-[0.2em] text-zinc-500">Capability</div>
            <div className="p-4 text-[10px] font-['JetBrains_Mono'] uppercase tracking-[0.2em] text-[#FF5E1A] border-l border-[#27272A]">AI Tuner Pro</div>
            <div className="p-4 text-[10px] font-['JetBrains_Mono'] uppercase tracking-[0.2em] text-zinc-600 border-l border-[#27272A]">Legacy tools</div>
          </div>
          {rows.map((r, i) => (
            <div key={r.feature} className={`grid grid-cols-[1.4fr_1fr_1fr] ${i % 2 === 0 ? 'bg-[#121212]' : 'bg-[#0F0F0F]'} ${i < rows.length - 1 ? 'border-b border-[#27272A]' : ''}`}>
              <div className="p-4 text-sm text-white font-medium">{r.feature}</div>
              <div className="p-4 border-l border-[#27272A] flex items-start gap-2 text-sm">
                <r.usIcon className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" />
                <span className="text-zinc-200">{r.us}</span>
              </div>
              <div className="p-4 border-l border-[#27272A] flex items-start gap-2 text-sm">
                <r.themIcon className="w-4 h-4 text-zinc-600 flex-shrink-0 mt-0.5" />
                <span className="text-zinc-500">{r.them}</span>
              </div>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}

// ========== OUR PROMISE ==========
function Promise() {
  const promises = [
    { icon: Lock, title: 'Full original backup before every flash', text: 'Your stock binary is archived in GridFS the moment you upload it. One-click rollback, forever.' },
    { icon: ShieldCheck, title: 'Safety checks on every map change', text: 'Rev limits, boost ceilings, AFR targets, knock protection — validated before any tune ships.' },
    { icon: RefreshCw, title: 'Continuous improvement from real data', text: 'Every datalog refines the model. Every contributed definition improves coverage for the next tuner.' },
    { icon: Heart, title: 'Transparent and community-driven', text: 'No black boxes. Every byte change is explained. Every KB source is cited. You own your calibration.' },
  ];
  return (
    <section className="py-24 bg-[#0A0A0A]">
      <div className="container mx-auto px-6">
        <motion.div {...FADE_UP} className="max-w-2xl mb-12">
          <div className="text-xs font-['JetBrains_Mono'] uppercase tracking-[0.2em] text-[#007AFF] mb-3">// Our Promise</div>
          <h2 className="font-['Outfit'] text-3xl sm:text-5xl font-black tracking-tighter text-white">
            Four commitments we don't compromise on.
          </h2>
        </motion.div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 max-w-5xl">
          {promises.map((p, i) => (
            <motion.div
              key={p.title}
              {...FADE_UP}
              transition={{ duration: 0.6, delay: i * 0.08 }}
              className="group flex items-start gap-5 p-6 bg-[#121212] border border-[#27272A] rounded-sm hover:border-[#FF5E1A]/40 transition-all"
            >
              <div className="w-11 h-11 flex-shrink-0 flex items-center justify-center rounded-sm bg-[#FF5E1A]/10 border border-[#FF5E1A]/30 group-hover:bg-[#FF5E1A]/20 transition-colors">
                <p.icon className="w-5 h-5 text-[#FF5E1A]" />
              </div>
              <div className="min-w-0">
                <h3 className="font-['Outfit'] text-base font-bold text-white mb-1.5">{p.title}</h3>
                <p className="text-sm text-zinc-400 leading-relaxed">{p.text}</p>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Trust strip */}
        <motion.div {...FADE_UP} className="mt-14 flex flex-wrap items-center gap-x-8 gap-y-3 justify-center border-y border-[#18181B] py-6 text-xs font-['JetBrains_Mono'] uppercase tracking-[0.15em] text-zinc-500">
          <span className="flex items-center gap-2"><Lock className="w-3.5 h-3.5 text-[#007AFF]" />Secure uploads (GridFS)</span>
          <span className="text-zinc-800 hidden sm:inline">•</span>
          <span className="flex items-center gap-2"><ShieldCheck className="w-3.5 h-3.5 text-[#007AFF]" />0 bricked ECUs</span>
          <span className="text-zinc-800 hidden sm:inline">•</span>
          <span className="flex items-center gap-2"><Award className="w-3.5 h-3.5 text-[#007AFF]" />Real datalog-verified gains</span>
          <span className="text-zinc-800 hidden sm:inline">•</span>
          <span className="flex items-center gap-2"><Gauge className="w-3.5 h-3.5 text-[#007AFF]" />Rollback anytime</span>
        </motion.div>
      </div>
    </section>
  );
}

// ========== CTA ==========
function AboutCTA({ onUpload }) {
  return (
    <section className="py-24 bg-gradient-to-b from-[#0A0A0A] via-[#050505] to-[#050505] relative overflow-hidden">
      <motion.div
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] rounded-full bg-[#FF5E1A]/10 blur-[120px]"
        animate={{ opacity: [0.4, 0.7, 0.4] }}
        transition={{ duration: 5, repeat: Infinity }}
      />
      <div className="relative container mx-auto px-6">
        <motion.div {...FADE_UP} className="max-w-3xl mx-auto text-center">
          <Rocket className="w-10 h-10 text-[#FF5E1A] mx-auto mb-6" />
          <h2 className="font-['Outfit'] text-3xl sm:text-5xl font-black tracking-tighter text-white mb-5">
            Ready to tune anything?
          </h2>
          <p className="text-lg text-zinc-300 mb-10 max-w-xl mx-auto">
            Upload your first bin free. Hybrid database matches in seconds, AI discovers the rest, and you iterate until the tune is yours.
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <button
              onClick={onUpload}
              data-testid="about-upload-bin-btn"
              className="group relative px-8 py-4 bg-[#FF5E1A] hover:bg-[#FF7A42] text-white font-bold text-sm tracking-[0.12em] uppercase rounded-sm transition-all duration-200 hover:-translate-y-0.5 hover:shadow-[0_0_45px_rgba(255,94,26,0.4)] flex items-center gap-2"
            >
              <UploadCloud className="w-4 h-4" />
              Upload Your First Bin — Free
              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
            </button>
          </div>
          <p className="text-xs text-zinc-600 font-['JetBrains_Mono'] uppercase tracking-wider mt-6">
            Any vehicle • Any ECU • No templates
          </p>
        </motion.div>
      </div>
    </section>
  );
}

// ========== ROOT ==========
export default function UniversalAbout({ onUpload }) {
  return (
    <div id="about" className="bg-[#0A0A0A]" data-testid="universal-about">
      <AboutHero />
      <FounderStory />
      <Mission />
      <HybridAI />
      <Differentiators />
      <Promise />
      <AboutCTA onUpload={onUpload} />
    </div>
  );
}
